import React, { useState } from "react";
import { CheckCircle2, AlertTriangle, ChevronDown, ChevronUp } from "lucide-react";
import { fmt } from "@/lib/mis-utils";

/**
 * Cross-view safety check.
 *
 * Compares the combined period totals (from `data.pl.*` / `data.balance_sheet.*`)
 * against the sum of per-month numbers in `data.monthly_summary`. Any drift
 * indicates a math discrepancy between the engine's combined and MoM pipelines
 * — exactly the "dark pattern" the user flagged previously.
 *
 * Renders nothing for single-month windows (nothing to tie out).
 */
const TOL = 1.0; // ₹1 rounding tolerance

function _check(label, combined, monthlySum) {
  const diff = (combined || 0) - (monthlySum || 0);
  return {
    label,
    combined: combined || 0,
    monthlySum: monthlySum || 0,
    diff,
    pass: Math.abs(diff) < TOL,
  };
}

export default function MISTieOutPanel({ data }) {
  const [open, setOpen] = useState(false);
  const ms = data?.monthly_summary || [];
  if (ms.length < 2) return null; // nothing to compare for single-month

  const sum = (k) => ms.reduce((acc, m) => acc + (m[k] || 0), 0);
  const checks = [
    _check("Revenue", data.pl?.total_revenue, sum("revenue")),
    _check("Direct Costs", data.pl?.total_direct_costs, sum("direct_costs")),
    _check("Gross Profit", data.pl?.gross_profit, sum("gross_profit")),
    _check("Employee Costs", data.pl?.total_employee_costs, sum("employee_costs")),
    _check("Overheads", data.pl?.total_overheads, sum("overheads")),
    _check("EBITDA", data.pl?.ebitda, sum("ebitda")),
    _check("Finance Costs", data.pl?.total_finance_costs, sum("finance_costs")),
    _check("Depreciation", data.pl?.total_depreciation, sum("depreciation")),
    _check("PBT", data.pl?.pbt, sum("pbt")),
  ];

  const failed = checks.filter((c) => !c.pass);
  const allPass = failed.length === 0;

  return (
    <div
      data-testid="mis-tieout-panel"
      className={`mb-4 rounded-lg border ${
        allPass
          ? "border-emerald-200 bg-emerald-50/40"
          : "border-amber-300 bg-amber-50/60"
      }`}
    >
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-2.5"
        data-testid="mis-tieout-toggle"
      >
        <div className="flex items-center gap-2">
          {allPass ? (
            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
          ) : (
            <AlertTriangle className="w-4 h-4 text-amber-600" />
          )}
          <span className="text-[12px] font-bold tracking-tight text-zinc-800">
            {allPass
              ? `Tie-out check passed (${checks.length}/${checks.length}) — ΣMonthly matches Combined for ${ms.length} months`
              : `Tie-out check: ${failed.length} discrepancy${failed.length === 1 ? "" : "ies"} detected`}
          </span>
        </div>
        {open ? (
          <ChevronUp className="w-3.5 h-3.5 text-zinc-500" />
        ) : (
          <ChevronDown className="w-3.5 h-3.5 text-zinc-500" />
        )}
      </button>

      {open && (
        <div className="px-4 pb-3" data-testid="mis-tieout-detail">
          <div className="overflow-x-auto">
            <table className="w-full text-[11px]">
              <thead>
                <tr className="text-zinc-500 border-b border-zinc-200">
                  <th className="text-left font-semibold py-1.5 pr-3">Metric</th>
                  <th className="text-right font-semibold py-1.5 px-2">Combined</th>
                  <th className="text-right font-semibold py-1.5 px-2">Σ Monthly</th>
                  <th className="text-right font-semibold py-1.5 pl-2">Difference</th>
                  <th className="text-center font-semibold py-1.5 pl-2 w-16">Status</th>
                </tr>
              </thead>
              <tbody className="font-mono tabular-nums">
                {checks.map((c) => (
                  <tr
                    key={c.label}
                    data-testid={`tieout-row-${c.label.replace(/\s+/g, "-").toLowerCase()}`}
                    className="border-b border-zinc-100 last:border-0"
                  >
                    <td className="py-1 pr-3 font-sans text-zinc-700">{c.label}</td>
                    <td className="text-right py-1 px-2">{fmt(c.combined)}</td>
                    <td className="text-right py-1 px-2">{fmt(c.monthlySum)}</td>
                    <td className={`text-right py-1 pl-2 ${
                      c.pass ? "text-zinc-400" : "text-amber-700 font-bold"
                    }`}>
                      {c.pass ? "0.00" : fmt(c.diff)}
                    </td>
                    <td className="text-center py-1 pl-2">
                      {c.pass ? (
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 inline" />
                      ) : (
                        <AlertTriangle className="w-3.5 h-3.5 text-amber-500 inline" />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!allPass && (
            <p className="text-[10.5px] text-zinc-500 mt-2 leading-relaxed">
              A non-zero difference means the engine&apos;s combined-period pipeline
              and the month-by-month pipeline produced different totals.
              Common causes: TB ledger missing in one month, date-boundary
              filter mismatch, stock adjustment not chaining across months.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
